import { createSelector } from '@reduxjs/toolkit';
import { appInsights } from '@/telemetry/client';
import urlsService from '@/services/urls-service';
import { baseApi } from './baseApi';
import type * as Types from '@/pages/winkelwagen/types';
import type { AppRootState } from '@/configureStore';

export const CACHE_TAG = 'CART';

export interface IAddToCartRequest {
  items: {
    variantCode: string;
    unitOfMeasureCode: string;
    quantity: number;
  }[];
}

export interface IAddSingleItemToCartRequest {
  variantCode: string;
  unitOfMeasureCode: string;
  quantity: number;
}

export interface IDeleteCartItemRequest {
  variantCode: string;
}

export interface IUpdateCartItemRequest {
  variantCode: string;
  unitOfMeasureCode: string;
  quantity: number;
  onAccount: boolean;
  reference?: string;
}

export interface IGetCartCountResponse {
  totalPieces: number;
  noOfLines: number;
  cartKey: string;
}

export interface IGetCartResponse {
  lines: Types.Product[];
  address: {
    code: string;
    name: string;
    address: string;
    postalCode: string;
    city: string;
  };
  costs: Types.SummaryTotals;
  totalPieces: number;
  noOfLines: number;
  shipmentMethodCode: string;
}

export interface ICartReplaceProductRequest {
  variantCode: string;
  replaceWith: string;
}

export const cartApi = baseApi.enhanceEndpoints({ addTagTypes: [CACHE_TAG] }).injectEndpoints({
  endpoints: (builder) => ({
    addToCart: builder.mutation<any, IAddToCartRequest>({
      query: ({ items }) => {
        const requestBody = {
          lines: items.map((item) => ({
            variantCode: item.variantCode,
            // Strip the variantcode + semicolon
            unitOfMeasureCode: item.unitOfMeasureCode.replace(`${item.variantCode}:`, ''),
            quantity: item.quantity
          }))
        };
        return {
          url: urlsService.getUrl('/cart/additems'),
          method: 'POST',
          body: requestBody
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    addSingleItemToCart: builder.mutation<any, IAddSingleItemToCartRequest>({
      query: ({ variantCode, unitOfMeasureCode, quantity }) => {
        const requestBody = {
          variantCode,
          unitOfMeasureCode,
          quantity
        };
        return {
          url: urlsService.getUrl('/cart/additem'),
          method: 'POST',
          body: requestBody
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    getCartCount: builder.query<IGetCartCountResponse, void>({
      query: () => urlsService.getUrl('/cart/getCount'),
      keepUnusedDataFor: 99999, // Do not remove cache after fixed timeout
      providesTags: [CACHE_TAG]
    }),
    getCart: builder.query<IGetCartResponse, void>({
      query: () => urlsService.getUrl('/cart/current'),
      transformResponse: (data: IGetCartResponse) => {
        return {
          ...data,
          lines: data.lines.map((line) => ({
            ...line,
            variantCode: line.variant.code,
            variantTitle: line.variant.title,
            //@ts-expect-error: in the API response this field is still called 'customerReference'
            reference: line.customerReference ?? line.reference
          }))
        };
      },
      keepUnusedDataFor: 99999, // Do not remove cache after fixed timeout
      providesTags: [CACHE_TAG]
    }),
    deleteCartItem: builder.mutation<IGetCartResponse, IDeleteCartItemRequest>({
      query: ({ variantCode }) => {
        const requestBody = {
          variantCode: variantCode
        };
        return {
          url: urlsService.getUrl('/cart/deleteItem'),
          method: 'POST',
          body: requestBody
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    updateCartItem: builder.mutation<IGetCartResponse, IUpdateCartItemRequest>({
      query: ({ variantCode, unitOfMeasureCode, quantity, onAccount, reference }) => {
        const requestBody = {
          variantCode,
          unitOfMeasureCode,
          quantity,
          onAccount,
          reference
        };
        return {
          url: urlsService.getUrl('/cart/updateItem'),
          method: 'POST',
          body: requestBody
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    replaceCartItem: builder.mutation<IGetCartResponse, ICartReplaceProductRequest>({
      query: (data) => {
        return {
          url: urlsService.getUrl('/cart/replaceItem'),
          method: 'POST',
          body: data
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    }),
    updateShippingMethod: builder.mutation({
      query: (methodCode) => {
        return {
          url: urlsService.getUrl(`/cart/updateshippingmethod/${methodCode}`),
          method: 'POST'
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData('getCart', undefined, () => {
              return data;
            })
          );
        } catch (error) {
          appInsights.trackException({ exception: error as Error });
        }
      }
    })
  })
});

export const {
  useGetCartQuery,
  useGetCartCountQuery,
  useAddToCartMutation,
  useAddSingleItemToCartMutation,
  useUpdateShippingMethodMutation,
  useReplaceCartItemMutation,
  useDeleteCartItemMutation,
  useUpdateCartItemMutation,
  useLazyGetCartQuery,
  useLazyGetCartCountQuery
} = cartApi;

export const cartSelector = cartApi.endpoints.getCart.select();

export function selectCart(state: AppRootState) {
  const cart = cartSelector(state ?? []);
  return cart;
}

export const selectCartTotal = (state: AppRootState) => selectCart(state)?.data?.totalPieces;
export const selectLines = (state: AppRootState) => cartSelector(state).data?.lines;
export const selectAddress = (state: AppRootState) => cartSelector(state).data?.address;
export const selectCosts = (state: AppRootState) => cartSelector(state).data?.costs;
export const selectShipmentMethodCode = (state: AppRootState) => cartSelector(state).data?.shipmentMethodCode;

/* eslint-disable prettier/prettier */
export const searchCartItems = createSelector(
  [selectLines, (_, searchTerm) => searchTerm],
  (lines, searchTerm) => {
    if (!searchTerm) {
      return [];
    }
    const matches = lines?.filter(line => {
      const matchCode = line.variantCode.includes(searchTerm.toLowerCase());
      const matchesTitle = line.variant.title.toLowerCase().includes(searchTerm.toLowerCase());
      return matchCode || matchesTitle;
    });

    return matches?.length ? matches : [];
  }
);
/* eslint-enable prettier/prettier */
